// API Service - Axios wrapper for backend communication
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Unauthorized - clear token and redirect to login
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
  updateMe: (data) => api.put('/auth/me', data),
};

// Vehicles API
export const vehiclesAPI = {
  getAll: () => api.get('/vehicles'),
  getOne: (id) => api.get(`/vehicles/${id}`),
  create: (data) => api.post('/vehicles', data),
  update: (id, data) => api.put(`/vehicles/${id}`, data),
  delete: (id) => api.delete(`/vehicles/${id}`),
};

// Trips API
export const tripsAPI = {
  getAll: (params) => api.get('/trips', { params }),
  getOne: (id) => api.get(`/trips/${id}`),
  create: (data) => api.post('/trips', data),
  update: (id, data) => api.put(`/trips/${id}`, data),
  delete: (id) => api.delete(`/trips/${id}`),
};

// Expenses API
export const expensesAPI = {
  getAll: (params) => api.get('/expenses', { params }),
  getOne: (id) => api.get(`/expenses/${id}`),
  create: (data) => api.post('/expenses', data),
  update: (id, data) => api.put(`/expenses/${id}`, data),
  delete: (id) => api.delete(`/expenses/${id}`),
  scanReceipt: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/expenses/scan-receipt', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Invoices API
export const invoicesAPI = {
  getAll: (params) => api.get('/invoices', { params }),
  getOne: (id) => api.get(`/invoices/${id}`),
  create: (data) => api.post('/invoices', data),
  update: (id, data) => api.put(`/invoices/${id}`, data),
  delete: (id) => api.delete(`/invoices/${id}`),
};

// Drivers API
export const driversAPI = {
  getAll: () => api.get('/drivers'),
  getOne: (id) => api.get(`/drivers/${id}`),
  create: (data) => api.post('/drivers', data),
  update: (id, data) => api.put(`/drivers/${id}`, data),
  delete: (id) => api.delete(`/drivers/${id}`),
};

// Assigned Trips API
export const assignedTripsAPI = {
  getAll: () => api.get('/assigned-trips'),
  create: (data) => api.post('/assigned-trips', data),
};

// Stats API
export const statsAPI = {
  getDashboard: () => api.get('/stats/dashboard'),
  getMonthly: (year, month) => api.get('/stats/monthly', { params: { year, month } }),
};

// Subscriptions API
export const subscriptionsAPI = {
  getPlans: () => api.get('/subscriptions/plans'),
  getStatus: () => api.get('/subscription/status'),
  createCheckout: (packageId, vehicleCount = null) => api.post('/subscriptions/checkout', {
    package_id: packageId,
    origin_url: window.location.origin,
    vehicle_count: vehicleCount
  }),
  checkStatus: (sessionId) => api.get(`/subscriptions/status/${sessionId}`),
};

// Exports API
export const exportsAPI = {
  invoicePdf: (invoiceId) => api.get(`/invoices/${invoiceId}/pdf`, { responseType: 'blob' }),
  accountingPdf: (year, month) => api.get('/exports/accounting/pdf', { 
    params: { year, month },
    responseType: 'blob'
  }),
  accountingExcel: (year, month) => api.get('/exports/accounting/excel', {
    params: { year, month },
    responseType: 'blob'
  }),
  yearlyPdf: (year) => api.get('/exports/yearly/pdf', {
    params: { year },
    responseType: 'blob'
  }),
};

// Email API
export const emailAPI = {
  sendInvoice: (invoiceId, recipientEmail) => api.post(`/invoices/${invoiceId}/send-email`, {
    recipient_email: recipientEmail
  }),
};

// Currency API
export const currencyAPI = {
  getAll: () => api.get('/currencies'),
  convert: (amount, fromCurrency, toCurrency) => api.get('/convert', {
    params: { amount, from_currency: fromCurrency, to_currency: toCurrency }
  }),
};

// Accountant API
export const accountantAPI = {
  getYearlySummary: (year) => api.get('/accountant/yearly-summary', { params: { year } }),
};

// Fleet Dashboard API  
export const fleetDashboardAPI = {
  getDashboard: () => api.get('/fleet/dashboard'),
};

export default api;
